import React, { useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { DeferredRender, RenderMode } from '@zillow/react-deferred-hydration';
import { RecentSearchClient } from '@zillow/recent-search-fetcher';
import { SearchBox, SRPNavigationSubmitHandler } from '@zillow/search-box';
import { useMobileState, useViewerState } from '@zillow/shopper-platform-config';
import { track } from '@zillow/universal-analytics';

import { graphqlClientId } from '../../constants/applicationConstants';
import { useGlobalStoreState } from '../../contexts/GlobalStoreProvider';
import type { SearchBoxProps, SearchLocation, SearchTerm } from '../../types/searchBox';
import type { InterstialProps } from '../Interstitial/Interstitial';
import Interstitial, { isInterstitialEnabled } from '../Interstitial/Interstitial';

const SEARCHBOX_PLATFORM_CONFIG = {
    // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
    DESKTOP: {
        placeholderText: 'Enter an address, neighborhood, city, or ZIP code',
        supportsLocationIcon: false,
    },
    // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
    MOBILE: {
        placeholderText: 'Address, neighborhood, city, ZIP',
        supportsLocationIcon: false,
    },
};

const searchBoxGAEventsConfig: SearchBoxProps['gaEventsConfig'] = {
    category: 'searchbox',
    // eslint-disable-next-line arrow-body-style
    getClickActionName: (category) => {
        return category === 'RAW_SEARCH' ? 'searchbox/submit' : 'searchbox/autocomplete';
    },
    getFocusActionName: () => 'searchbox/open',
};

// Start with number followed by 2 or more alphanumeric words starting with space or tab
const genericAddressPattern = /^\d+(([ \t]+[a-z0-9]+){2,})/i;

const SearchBoxWrapper: React.FC<{ size: SearchBoxProps['size'] }> = ({ size }) => {
    const isMobile = useMobileState();
    const { isLoggedIn } = useViewerState();

    const client = useApolloClient();

    const {
        centroid,
        guid,
        isNLSEnabled,
        isNLSUiUpdatesOnlyEnabled,
        isNLSDelayedSemanticAcEnabled,
        autocompleteUrl,
    } = useGlobalStoreState();

    const [interstitialProps, setInterstitialProps] = useState<InterstialProps | null>(null);

    const handleSubmit = (searchTerm: SearchTerm, searchLocation: SearchLocation) => {
        if (
            isInterstitialEnabled({ loggedIn: isLoggedIn }) &&
            !isLoggedIn &&
            searchTerm.category !== 'ADDRESS' &&
            // exclude semantic search from interstitial
            searchTerm.category !== 'SEMANTIC' &&
            (searchTerm.category !== 'RAW_SEARCH' ||
                searchTerm.displayName === '' ||
                !searchTerm.displayName?.match(genericAddressPattern))
        ) {
            setInterstitialProps({
                searchLocation,
                searchTerm,
            });
            track({
                action: 'Listing_Type_Interstitial_Eligible',
                category: 'ACT',
            });
            // when the user visits the home page for the very first time
            // and perform the first search query, we launch the interstitial experience,
            // which prevent the recent search record from being saved,
            // hence explicitly calling this here
            // so first search query is recorded.
            RecentSearchClient.addToRecentSearches(searchTerm);

            return;
        }
        SRPNavigationSubmitHandler(searchTerm, searchLocation);
    };

    return (
        <React.Fragment>
            <SearchBox
                size={size}
                shouldShowSavedHomes={isLoggedIn}
                shouldShowSavedSearches={isLoggedIn}
                handleSubmit={handleSubmit}
                showAdornments
                gaEventsConfig={searchBoxGAEventsConfig}
                platformConfig={SEARCHBOX_PLATFORM_CONFIG}
                platform={isMobile ? 'MOBILE' : 'DESKTOP'}
                userSearchContext="FOR_SALE"
                enableNaturalLanguageSearch={
                    isNLSEnabled || isNLSUiUpdatesOnlyEnabled || isNLSDelayedSemanticAcEnabled
                }
                enableNlsUiUpdatesOnly={isNLSUiUpdatesOnlyEnabled}
                enableNlsDelayedSemanticAc={isNLSDelayedSemanticAcEnabled}
                zgGraphClient={client}
                regionAddressAutocompleteURL={autocompleteUrl}
                recentSearchLocalStorageKey="recentSearchesEnhanced"
                allowEmptyInitialClickstreamBlocks
                // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
                clickstreamTriggerOverrides={{ trigger_location_nm: 'home' }}
                centroid={centroid}
                clientId={graphqlClientId}
                abKey={guid}
            />
            {!!interstitialProps && (
                <DeferredRender mode={RenderMode.RENDER_CLIENT_SIDE_ON_IMMEDIATE}>
                    <Interstitial {...interstitialProps} />
                </DeferredRender>
            )}
        </React.Fragment>
    );
};

export default SearchBoxWrapper;
